import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { useCallback, useEffect, useState } from "react";
import {
  getProjectVotes,
  VotingPeriodProject,
  Vote,
} from "utils/apiService/PinkSale";

// Format date
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

// Get chain name
const getChainName = (chainId: number) => {
  const chainMap: { [key: number]: string } = {
    1: "Ethereum",
    56: "BSC",
    501424: "Solana",
  };
  return chainMap[chainId] || `Chain ${chainId}`;
};

interface ProjectVotesModalProps {
  isOpen: boolean;
  onClose: () => void;
  votingPeriodId: string;
  project: VotingPeriodProject | null;
}

const ProjectVotesModal = ({
  isOpen,
  onClose,
  votingPeriodId,
  project,
}: ProjectVotesModalProps) => {
  const toast = useToast();
  const [votes, setVotes] = useState<Vote[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState<{
    total: number;
    totalPages: number;
  }>({ total: 0, totalPages: 0 });

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied",
      description: "Copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  const fetchVotes = useCallback(
    async (pageNumber: number) => {
      if (!votingPeriodId || !project) return;

      try {
        setLoading(true);
        const votesData = await getProjectVotes(
          votingPeriodId,
          project.launchpadId,
          {
            page: pageNumber,
            limit,
          }
        );
        setVotes(votesData.votes);
        setPagination({
          total: votesData.total,
          totalPages: votesData.totalPages,
        });
        setPage(pageNumber);
      } catch (error: any) {
        toast({
          title: "Error",
          description: error?.message || "Failed to fetch project votes",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    },
    [votingPeriodId, project, limit, toast]
  );

  const handlePageChange = async (newPage: number) => {
    await fetchVotes(newPage);
  };

  const handleReload = async () => {
    await fetchVotes(page);
    toast({
      title: "Success",
      description: "Project votes refreshed",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
  };

  // Fetch votes when modal opens or project changes
  useEffect(() => {
    if (isOpen && project && votingPeriodId) {
      setPage(1);
      fetchVotes(1);
    }
  }, [isOpen, project?._id, votingPeriodId, fetchVotes]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          Votes for {project?.launchpad?.token?.name || "N/A"} (
          {project?.launchpad?.token?.symbol || "N/A"})
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {loading ? (
            <Center py={10}>
              <Spinner size="xl" />
            </Center>
          ) : (
            <VStack align="stretch" spacing={4}>
              <Flex justify="space-between" align="center">
                <Text fontSize="sm" color="gray.500">
                  Total Votes: {pagination.total}
                </Text>
                <HStack spacing={3}>
                  <Text fontSize="sm" color="gray.500">
                    Page {page} of {pagination.totalPages || 1}
                  </Text>
                  <IconButton
                    aria-label="Reload project votes"
                    icon={<RepeatIcon />}
                    onClick={handleReload}
                    isLoading={loading}
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                  />
                </HStack>
              </Flex>
              <Box overflowX="auto">
                <Table variant="simple" size="sm" bg="white" borderRadius="md">
                  <Thead>
                    <Tr>
                      <Th>Voter Address</Th>
                      <Th>Campaign ID</Th>
                      <Th>Chain</Th>
                      <Th>Voting Power</Th>
                      <Th>Created At</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {votes.length === 0 ? (
                      <Tr>
                        <Td colSpan={5} textAlign="center" py={10}>
                          <Text color="gray.500">No votes found</Text>
                        </Td>
                      </Tr>
                    ) : (
                      votes.map((vote) => (
                        <Tr key={vote._id}>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="copy"
                              _hover={{ color: "blue.500" }}
                              onClick={() => handleCopy(vote.voterAddress)}
                            >
                              {vote.voterAddress.slice(0, 10)}...
                              {vote.voterAddress.slice(-8)}
                            </Text>
                          </Td>
                          <Td>
                            <Text
                              fontSize="xs"
                              fontFamily="mono"
                              cursor="copy"
                              _hover={{ color: "blue.500" }}
                              onClick={() => handleCopy(vote.campaignId)}
                            >
                              {vote.campaignId.slice(0, 10)}...
                            </Text>
                          </Td>
                          <Td>{getChainName(vote.chainId)}</Td>
                          <Td>{vote.votingPower.toLocaleString()}</Td>
                          <Td fontSize="xs">{formatDate(vote.createdAt)}</Td>
                        </Tr>
                      ))
                    )}
                  </Tbody>
                </Table>
              </Box>
              {/* Pagination Controls */}
              {pagination.totalPages > 1 && (
                <Flex justify="center" align="center" gap={4} mt={4}>
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(page - 1)}
                    isDisabled={page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <Text fontSize="sm">
                    Page {page} of {pagination.totalPages}
                  </Text>
                  <Button
                    size="sm"
                    onClick={() => handlePageChange(page + 1)}
                    isDisabled={page >= pagination.totalPages || loading}
                  >
                    Next
                  </Button>
                </Flex>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button colorScheme="gray" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ProjectVotesModal;
